/* SRx public landing attribution, measurement v1.
 * Adds generic context to the existing dataLayer; no GA initialization, purchase
 * sender, clinical request interception, field reading, or consent acceptance.
 */
(() => {
  'use strict';

  const documentRoot = document.documentElement;
  if (!documentRoot || documentRoot.dataset.srxAttributionInitialized === 'v1') return;
  documentRoot.dataset.srxAttributionInitialized = 'v1';

  const STORAGE_KEY = 'srx_lp_context';
  const VERSION = 'v1';
  const TTL = 24 * 60 * 60 * 1000;
  const EXPOSURE_WINDOW = 30 * 60 * 1000;
  const INTENT_WINDOW = 12 * 1000;
  const READY_WINDOW = 60 * 1000;
  const RELEASES = Object.freeze({
    control: '2026-10-13-control',
    seguridad: '2026-10-13-seguridad'
  });
  const productionDomain = location.hostname === 'srreceta.com' || location.hostname.endsWith('.srreceta.com');
  const cookieScope = '; Path=/; SameSite=Lax; Secure' + (productionDomain ? '; Domain=.srreceta.com' : '');
  const qa = new URLSearchParams(location.search).get('srx_qa') === '1';

  let allowed = false;
  let context = null;
  let portal = null;
  let portalObserver = null;
  let discoveryObserver = null;
  let previouslyVisible = false;
  let trustedIntentAt = 0;
  let pendingOpen = null;
  let directJourney = null;
  let intentTimer = null;
  let cartTimer = null;
  let cartInFlight = false;
  let cartRequest = null;
  let cartErasure = null;
  let stopped = false;
  let wrappedLayer = null;
  let installedPush = null;
  let previousPush = null;
  let privacyLoading = false;
  let privacyAttempts = 0;
  let privacyAttemptAt = 0;
  let privacyTimer = null;
  let denialErasureQueued = false;

  function consentState() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return -1;
      return privacy.analyticsProcessingAllowed() === true ? 1 : 0;
    } catch (_) { return -1; }
  }

  function hasPermission() { return !stopped && consentState() === 1; }

  function loadPrivacyAPI() {
    const shopify = window.Shopify;
    if (stopped || consentState() !== -1 || !shopify || typeof shopify.loadFeatures !== 'function' || privacyLoading || privacyAttempts >= 3 || (privacyAttemptAt && Date.now() - privacyAttemptAt < 5000)) return;
    privacyLoading = true;
    privacyAttempts += 1;
    privacyAttemptAt = Date.now();
    privacyTimer = setTimeout(() => { privacyLoading = false; privacyTimer = null; }, 10000);
    try {
      // Official API loader only; never record, set, or accept visitor consent.
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
        clearTimeout(privacyTimer);
        privacyTimer = null;
        privacyLoading = false;
        // Defer even a synchronous loader callback until the current consent
        // check has finished. API load success is not analytics permission.
        if (!error && !stopped) setTimeout(syncConsent, 0);
      });
    } catch (_) {
      clearTimeout(privacyTimer);
      privacyTimer = null;
      privacyLoading = false;
    }
  }

  function timestamp(value, now) {
    return Number.isSafeInteger(value) && value >= 0 && value <= now + 60000;
  }

  function validContext(value) {
    const now = Date.now();
    if (!value || typeof value !== 'object' || value.version !== 1) return null;
    const view = value.srx_landing_view;
    if (!Object.prototype.hasOwnProperty.call(RELEASES, view) || RELEASES[view] !== value.srx_page_revision) return null;
    if (value.srx_measurement_version !== VERSION || !['direct', 'resumed'].includes(value.srx_attribution_type)) return null;
    if (!['customer', 'qa'].includes(value.srx_traffic_type)) return null;
    if (typeof value.journey !== 'string' || !/^[a-f0-9-]{32,36}$/i.test(value.journey)) return null;
    if (![value.createdAt, value.startedAt, value.completedAt].every(item => timestamp(item, now))) return null;
    if (value.createdAt <= 0 || (value.startedAt && value.startedAt < value.createdAt) || (value.completedAt && (!value.startedAt || value.completedAt < value.startedAt))) return null;
    if (!Number.isSafeInteger(value.expiresAt) || value.expiresAt <= now || value.expiresAt > Math.max(value.createdAt, value.startedAt) + TTL) return null;
    const exposures = {};
    if (value.exposures && typeof value.exposures === 'object') {
      for (const name of Object.keys(RELEASES)) {
        const time = value.exposures[name];
        if (timestamp(time, now) && time > now - EXPOSURE_WINDOW) exposures[name] = time;
      }
    }
    // Reconstruct, rather than retain arbitrary cookie fields.
    return {
      version: 1,
      srx_landing_view: view,
      srx_page_revision: RELEASES[view],
      srx_attribution_type: value.srx_attribution_type,
      srx_measurement_version: VERSION,
      srx_traffic_type: value.srx_traffic_type,
      journey: value.journey,
      createdAt: value.createdAt,
      startedAt: value.startedAt,
      completedAt: value.completedAt,
      expiresAt: value.expiresAt,
      exposures,
      cartAttached: value.cartAttached === true,
      cartAttempts: Number.isInteger(value.cartAttempts) && value.cartAttempts >= 0 && value.cartAttempts <= 3 ? value.cartAttempts : 0
    };
  }

  function decodeContext(raw) {
    if (typeof raw !== 'string' || raw.length > 2048) return null;
    try { return validContext(JSON.parse(raw)); } catch (_) { return null; }
  }

  function cookieValue() {
    try {
      const item = document.cookie.split(';').map(part => part.trim()).find(part => part.startsWith(STORAGE_KEY + '='));
      return item ? decodeURIComponent(item.slice(STORAGE_KEY.length + 1)) : null;
    } catch (_) { return null; }
  }

  function readContext() {
    if (!hasPermission()) return null;
    const rawCookie = cookieValue();
    const fromCookie = decodeContext(rawCookie);
    if (fromCookie) return fromCookie;
    let rawLocal = null;
    try { rawLocal = window.localStorage.getItem(STORAGE_KEY); } catch (_) {}
    const fromLocal = decodeContext(rawLocal);
    if (fromLocal) return fromLocal;
    if (rawCookie || rawLocal) clearStorage();
    return null;
  }

  function persist() {
    if (!hasPermission() || !context) return;
    const safe = validContext(context);
    if (!safe) { context = null; clearStorage(); return; }
    context = safe;
    const raw = JSON.stringify(safe);
    const remaining = Math.max(1, Math.floor((safe.expiresAt - Date.now()) / 1000));
    let stored = false;
    try {
      document.cookie = STORAGE_KEY + '=' + encodeURIComponent(raw) + '; Max-Age=' + remaining + cookieScope;
      stored = cookieValue() === raw;
    } catch (_) {}
    try {
      if (stored) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, raw);
    } catch (_) {}
  }

  function clearStorage() {
    try { document.cookie = STORAGE_KEY + '=; Max-Age=0' + cookieScope; } catch (_) {}
    // Remove a host-only fallback from an earlier installation too.
    if (productionDomain) {
      try { document.cookie = STORAGE_KEY + '=; Max-Age=0; Path=/; SameSite=Lax; Secure'; } catch (_) {}
    }
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }

  function hasOwnedStorage() {
    // Presence checks only on our one key; no other cookie or value parsing.
    try { if (/(?:^|;\s*)srx_lp_context=/.test(document.cookie)) return true; } catch (_) {}
    try { return window.localStorage.getItem(STORAGE_KEY) !== null; } catch (_) { return false; }
  }

  function randomJourney() {
    try {
      if (typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    } catch (_) { return null; }
  }

  function rendered(element) {
    if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    try {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    } catch (_) { return false; }
  }

  function currentOrigin() {
    const root = document.getElementById('srx-design-root');
    if (!root || root.dataset.srxLocale !== 'es' || !rendered(root) || location.pathname !== '/products/alpha-para-el') return null;
    const view = Object.keys(RELEASES).find(name => RELEASES[name] === root.dataset.srxRelease);
    if (!view) return null;
    const requested = new URLSearchParams(location.search).getAll('view');
    if (requested.length > 1 || (requested.length === 1 && requested[0] !== view)) return null;
    return { view, revision: RELEASES[view] };
  }

  function metadata(originContext) {
    return {
      srx_landing_view: originContext.srx_landing_view,
      srx_page_revision: originContext.srx_page_revision,
      srx_attribution_type: originContext.srx_attribution_type,
      srx_measurement_version: VERSION,
      srx_traffic_type: originContext.srx_traffic_type
    };
  }

  function ensureLayer() {
    if (!hasPermission()) return;
    if (!window.dataLayer) window.dataLayer = [];
    const layer = window.dataLayer;
    if (!layer || typeof layer.push !== 'function' || (layer === wrappedLayer && layer.push === installedPush)) return;
    const prior = layer.push;
    function pushWithContext(...items) {
      let forwarded = items;
      const milestones = [];
      try {
        if (hasPermission()) {
          forwarded = items.map(item => {
            if (!item || typeof item !== 'object' || Array.isArray(item) || item.event !== 'prescription_form_complete' || item.form_name !== 'para_el') return item;
            const latest = readContext();
            if (latest && latest.startedAt) {
              latest.srx_attribution_type = directJourney === latest.journey ? 'direct' : 'resumed';
              context = latest;
            }
            const safe = validContext(context);
            if (!safe || !safe.startedAt || safe.completedAt) return item;
            context = safe;
            context.completedAt = Date.now();
            persist();
            const fields = metadata(context);
            milestones.push(fields);
            return { ...item, ...fields };
          });
        }
      } catch (_) { forwarded = items; }
      const result = prior.apply(this, forwarded);
      // Native messages retain their order and original push return value.
      // Call the captured push directly, avoiding our own wrapper recursion.
      if (hasPermission()) {
        for (const fields of milestones) {
          try { prior.call(this, { event: 'srx_evaluation_complete', ...fields }); } catch (_) {}
        }
      }
      return result;
    }
    wrappedLayer = layer;
    previousPush = prior;
    installedPush = pushWithContext;
    layer.push = pushWithContext;
  }

  function emit(event, fields) {
    if (!hasPermission()) return false;
    try {
      ensureLayer();
      window.dataLayer.push({ event, ...fields });
      return true;
    } catch (_) { return false; }
  }

  function createContext(origin) {
    const journey = randomJourney();
    if (!journey) return null;
    const now = Date.now();
    return {
      version: 1,
      srx_landing_view: origin.view,
      srx_page_revision: origin.revision,
      srx_attribution_type: 'direct',
      srx_measurement_version: VERSION,
      srx_traffic_type: qa ? 'qa' : 'customer',
      journey,
      createdAt: now,
      startedAt: 0,
      completedAt: 0,
      expiresAt: now + TTL,
      exposures: {},
      cartAttached: false,
      cartAttempts: 0
    };
  }

  function expose() {
    if (!hasPermission()) return;
    const origin = currentOrigin();
    if (!origin) return;
    const authoritative = readContext();
    if (authoritative && authoritative.startedAt) {
      // Another tab may have begun the journey after this page initialized.
      // Preserve its entire frozen origin/stage state; merge only our bounded
      // exposure timestamps before making an exposure-related storage write.
      const local = validContext(context);
      for (const name of Object.keys(RELEASES)) {
        if (local && local.exposures[name]) authoritative.exposures[name] = Math.max(authoritative.exposures[name] || 0, local.exposures[name]);
      }
      context = authoritative;
    } else if (!validContext(context)) context = authoritative || createContext(origin);
    if (!context) return;
    if (!context.startedAt) {
      context.srx_landing_view = origin.view;
      context.srx_page_revision = origin.revision;
      context.srx_attribution_type = 'direct';
      context.srx_traffic_type = qa ? 'qa' : 'customer';
    }
    const now = Date.now();
    if (context.exposures[origin.view] && now - context.exposures[origin.view] < EXPOSURE_WINDOW) return;
    const fields = {
      srx_landing_view: origin.view,
      srx_page_revision: origin.revision,
      srx_attribution_type: 'direct',
      srx_measurement_version: VERSION,
      srx_traffic_type: qa ? 'qa' : 'customer'
    };
    if (emit('srx_landing_exposure', fields)) {
      context.exposures[origin.view] = now;
      persist();
    }
  }

  function cartAttributes(value) {
    return {
      srx_lp_view: value.srx_landing_view,
      srx_lp_revision: value.srx_page_revision,
      srx_lp_journey: value.journey,
      srx_lp_started_at: String(value.startedAt),
      srx_lp_traffic_type: value.srx_traffic_type,
      srx_lp_measurement_version: VERSION
    };
  }

  function cartEndpoint() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(root || '') ? root : '/') + 'cart/update.js';
  }

  function attachCart() {
    if (!hasPermission() || typeof window.fetch !== 'function') return;
    if (cartInFlight || cartErasure) return;
    const safe = validContext(context);
    if (!safe || !safe.startedAt || safe.cartAttached || safe.cartAttempts >= 3) return;
    const journey = safe.journey;
    context.cartAttempts += 1;
    persist();
    cartInFlight = true;
    cartRequest = Promise.resolve().then(() => {
      if (!hasPermission()) return null;
      return window.fetch(cartEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify({ attributes: cartAttributes(safe) })
      });
    }).then(response => {
      if (response && response.ok === true && hasPermission() && context && context.journey === journey) {
        context.cartAttached = true;
        persist();
      }
    }).catch(() => {}).finally(() => {
      cartInFlight = false;
      if (hasPermission() && context && context.journey === journey && !context.cartAttached && context.cartAttempts < 3) {
        clearTimeout(cartTimer);
        cartTimer = setTimeout(() => { cartTimer = null; attachCart(); }, context.cartAttempts === 1 ? 1500 : 4500);
      } else if (hasPermission() && context && context.journey !== journey && context.startedAt) {
        attachCart();
      }
    });
  }

  function eraseCartContext() {
    if (typeof window.fetch !== 'function') return;
    const attributes = {};
    for (const key of ['srx_lp_view', 'srx_lp_revision', 'srx_lp_journey', 'srx_lp_started_at', 'srx_lp_traffic_type', 'srx_lp_measurement_version']) attributes[key] = '';
    // Erasure is permitted after withdrawal. Finish any already-authorized write
    // first, so a slow response cannot restore attribution after the erasure.
    const pending = cartRequest || Promise.resolve();
    cartErasure = pending.catch(() => {}).then(() => window.fetch(cartEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify({ attributes })
    })).catch(() => {}).finally(() => {
      cartErasure = null;
      attachCart();
    });
  }

  function portalHostVisible() {
    if (!portal || portal.classList.contains('hidden') || portal.hidden || portal.getAttribute('aria-hidden') === 'true') return false;
    try {
      const style = window.getComputedStyle(portal);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    } catch (_) { return false; }
    return true;
  }

  function renderedDialog() {
    if (!portalHostVisible()) return null;
    // The portal is a zero-height host for a fixed-position dialog. Its own
    // geometry does not establish visibility; measure the dialog instead.
    const dialog = portal.querySelector('[role="dialog"]');
    return rendered(dialog) ? dialog : null;
  }

  function interactiveDialog(dialog) {
    // Presence only: never read the control's name, text, value, or question.
    return !!dialog && !!dialog.querySelector('button:not([disabled]),a[href],input:not([type="hidden"]),select,textarea');
  }

  function checkPortal() {
    const dialog = renderedDialog();
    const open = !!dialog;
    const opened = open && !previouslyVisible;
    previouslyVisible = open;
    if (!open) { pendingOpen = null; return; }
    const now = Date.now();
    const origin = currentOrigin();
    const root = document.getElementById('srx-design-root');
    const matches = origin && root && portal.dataset.activeLocale === 'es' && root.dataset.srxProductId && portal.dataset.productId === root.dataset.srxProductId;
    if (!hasPermission() || !matches) { pendingOpen = null; return; }
    if (opened && trustedIntentAt && now - trustedIntentAt <= INTENT_WINDOW) {
      pendingOpen = { at: now, view: origin.view, revision: origin.revision };
      trustedIntentAt = 0;
    }
    if (!pendingOpen) return;
    if (now - pendingOpen.at > READY_WINDOW || pendingOpen.view !== origin.view || pendingOpen.revision !== origin.revision) { pendingOpen = null; return; }
    // A promptly opened native loading shell is authorized, but not a start.
    // Wait up to 60 seconds for its actual interactive dialog while it stays
    // open, matches the same origin, and analytics remains permitted.
    if (!interactiveDialog(dialog)) return;
    pendingOpen = null;
    const saved = readContext();
    if (saved && saved.startedAt) {
      saved.srx_attribution_type = directJourney === saved.journey ? 'direct' : 'resumed';
      context = saved; persist(); attachCart(); return;
    }
    if (!validContext(context)) context = saved || createContext(origin);
    if (!context || context.startedAt) return;
    context.srx_landing_view = origin.view;
    context.srx_page_revision = origin.revision;
    context.srx_attribution_type = 'direct';
    context.srx_traffic_type = qa ? 'qa' : 'customer';
    context.startedAt = Date.now();
    directJourney = context.journey;
    context.expiresAt = context.startedAt + TTL;
    // Persist first, so another visit or rapid reopening cannot create a second
    // attributed start. Failed analytics never delay the native dialog.
    persist();
    emit('srx_intake_start', metadata(context));
    trustedIntentAt = 0;
    attachCart();
  }

  function discoverPortal() {
    const found = document.querySelector('[data-app-layer-portal="form-portal"]');
    if (!found || found === portal) return;
    if (portalObserver) portalObserver.disconnect();
    portal = found;
    pendingOpen = null;
    previouslyVisible = !!renderedDialog();
    portalObserver = new MutationObserver(checkPortal);
    portalObserver.observe(portal, { attributes: true, attributeFilter: ['class', 'hidden', 'aria-hidden', 'style'], childList: true });
    if (discoveryObserver) discoveryObserver.disconnect();
  }

  function pollIntent() {
    checkPortal();
    const now = Date.now();
    if (!stopped && ((trustedIntentAt && now - trustedIntentAt <= INTENT_WINDOW) || (pendingOpen && now - pendingOpen.at <= READY_WINDOW))) intentTimer = setTimeout(pollIntent, 200);
  }

  function onClick(event) {
    if (!event.isTrusted || event.defaultPrevented || (event.button != null && event.button !== 0) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !hasPermission()) return;
    const target = event.target && typeof event.target.closest === 'function' ? event.target.closest('[data-srx-launch],[data-take-quiz-el]') : null;
    if (!target || target.disabled || target.getAttribute('aria-disabled') === 'true') return;
    const root = document.getElementById('srx-design-root');
    const native = document.querySelector('[data-product-main]');
    if (!((root && root.contains(target)) || (native && native.contains(target)))) return;
    discoverPortal();
    // Capture before the presentation adapter delegates a synthetic .click().
    // That delegated click must not reset intent or count a separate action.
    trustedIntentAt = Date.now();
    clearTimeout(intentTimer);
    intentTimer = setTimeout(pollIntent, 0);
  }

  function syncConsent() {
    if (stopped) return;
    const state = consentState();
    const next = state === 1;
    if (!next) {
      if (state === -1) loadPrivacyAPI();
      if (allowed || state === 0) {
        const hadStartedContext = context && context.startedAt;
        const ownedStorage = hasOwnedStorage();
        context = null;
        trustedIntentAt = 0;
        pendingOpen = null;
        directJourney = null;
        clearTimeout(intentTimer);
        clearTimeout(cartTimer);
        clearStorage();
        if (!denialErasureQueued && (hadStartedContext || ownedStorage)) {
          denialErasureQueued = true;
          eraseCartContext();
        }
      }
      allowed = false;
      return;
    }
    if (!allowed) {
      allowed = true;
      denialErasureQueued = false;
      context = readContext();
      if (context && context.startedAt) { context.srx_attribution_type = 'resumed'; persist(); }
    }
    ensureLayer();
    expose();
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('visitorConsentCollected', syncConsent);
  discoverPortal();
  if (!portal) {
    discoveryObserver = new MutationObserver(() => { discoverPortal(); syncConsent(); });
    discoveryObserver.observe(documentRoot, { childList: true, subtree: true });
  }
  syncConsent();
  // Covers late API/GTM availability and withdrawal without accepting consent.
  const maintenance = setInterval(() => { syncConsent(); discoverPortal(); }, 2000);
  window.addEventListener('pagehide', event => {
    pendingOpen = null;
    trustedIntentAt = 0;
    clearTimeout(intentTimer);
    intentTimer = null;
    // BFCache freezes this same document and its timers/observers. Keep the
    // bridge alive for its return; ordinary navigation still performs cleanup.
    if (event.persisted) return;
    stopped = true;
    clearInterval(maintenance);
    clearTimeout(intentTimer);
    clearTimeout(cartTimer);
    clearTimeout(privacyTimer);
    if (portalObserver) portalObserver.disconnect();
    if (discoveryObserver) discoveryObserver.disconnect();
    if (wrappedLayer && wrappedLayer.push === installedPush) wrappedLayer.push = previousPush;
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted || stopped) return;
    // A launch intent must never carry across navigation. Reload consent and
    // bounded stored context, which may have changed on the intervening page.
    trustedIntentAt = 0;
    pendingOpen = null;
    directJourney = null;
    clearTimeout(intentTimer);
    intentTimer = null;
    allowed = false;
    syncConsent();
    discoverPortal();
    previouslyVisible = !!renderedDialog();
  });
})();
